"use client";

import { Control, UseFormClearErrors, UseFormSetValue } from "react-hook-form";
import { RefObject, useEffect, useMemo, useState } from "react";
import Mailcheck from "mailcheck";
import { useTranslation } from "@/hooks/useTranslation";
import {
  QueryObserverResult,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useRouter } from "next/router";

import {
  PayPalScriptProvider,
  type ReactPayPalScriptOptions,
} from "@paypal/react-paypal-js";

import { CartResponse, updateCartAttributes } from "@/networking/carts.api";
import type { CheckoutFormData, ContactErrorsState } from "./checkout.types";
import FormTextInput from "@/components/ui/input/form-text-input";
import FormPhoneInput from "@/components/ui/input/form-phone-input";
import { isApiError } from "@/lib/api-helpers";
import { checkoutRedirect } from "@/lib/checkout-redirect";
import { cn, formatAddressLocalityLine, toPaymentAmount } from "@/lib/utils";
import { ADDITIONAL_VALID_EMAIL_TLDS } from "@/lib/constants";
import {
  confirmMagicLink,
  ConfirmMagicLinkInput,
  createMagicLink,
} from "@/networking/auth.api";
import VerificationCard from "./verification-card";
import { WarningAlert } from "@/components/ui/alert";
import FastlaneWatermark from "@/components/checkout/fastlane-watermark";
import { usePayPalCheckout } from "@/hooks/usePaypalCheckout";
import type { PayPalNamespace } from "@paypal/paypal-js";

import { checkoutCart } from "@/networking/checkout.api";
import { updateShippingMethod } from "@/networking/shipping.api";
import {
  createPaypalOrder,
  createPaypalPaymentMethod,
  getPaypalSdkToken,
} from "@/networking/fast-checkout/paypal.api";

// ===================================================================
//  SECTION 1: FASTLANE TYPE DEFINITIONS
// ===================================================================
const FASTLANE_NAMESPACE = "paypal_fastlane" as const;

type PayPalWithFastlane =
  | (PayPalNamespace & {
      Fastlane?: (config: FastlaneConfig) => Promise<FastlaneInstance>;
    })
  | null
  | undefined;

interface FastlaneConfig {
  locale: string;
  metadata: { geoLocOverride: string };
  shippingAddressOptions: { allowedLocations: string[] };
}

type AuthenticationState = "succeeded" | "cancelled" | "failed";

interface FastlanePaymentComponentInstance {
  render: (container: string) => Promise<void>;
  getPaymentToken: () => Promise<{ id: string }>;
}

interface SelectedAddress {
  name: {
    firstName?: string;
    lastName?: string;
  };
  address: {
    addressLine1?: string;
    addressLine2?: string;
    adminArea2?: string;
    adminArea1?: string;
    postalCode?: string;
    countryCode?: string;
  };
}

interface FastlaneProfileData {
  shippingAddress?: {
    name: {
      firstName?: string;
      lastName?: string;
      fullName?: string;
    };
    address: {
      addressLine1?: string;
      addressLine2?: string;
      adminArea2?: string;
      adminArea1?: string;
      postalCode?: string;
      countryCode?: string;
    };
    phoneNumber?: {
      nationalNumber?: string;
      countryCode?: string;
    };
  };
  card?: {
    id?: string;
    paymentSource?: {
      card?: {
        brand?: string;
        expiry?: string;
        lastDigits?: string;
        name?: string;
        networkTokenExchangeSupported?: boolean;
        billingAddress?: {
          addressLine1?: string;
          addressLine2?: string;
          adminArea2?: string;
          adminArea1?: string;
          postalCode?: string;
          countryCode?: string;
        };
        binDetails?: object;
      };
    };
  };
  name?: {
    firstName?: string;
    lastName?: string;
    fullName?: string;
  };
  phones?: Array<{
    nationalNumber?: string;
    countryCode?: string;
  }>;
}

interface FastlaneInstance {
  identity: {
    lookupCustomerByEmail: (
      email: string,
    ) => Promise<{ customerContextId?: string }>;
    triggerAuthenticationFlow: (customerContextId: string) => Promise<{
      authenticationState: AuthenticationState;
      profileData?: FastlaneProfileData;
    }>;
  };
  profile: {
    showShippingAddressSelector: () => Promise<{
      selectionChanged: boolean;
      selectedAddress: SelectedAddress;
    }>;
  };
  FastlanePaymentComponent: (options: {
    shippingAddress: object;
    options?: object;
  }) => Promise<FastlanePaymentComponentInstance>;
}

function hasPhone(
  cart: CartResponse["cart"],
): cart is CartResponse["cart"] & { phone?: string | null } {
  return "phone" in cart;
}

function waitForFastlane(
  namespace: string,
  { attempts = 40, interval = 150 } = {},
): Promise<PayPalWithFastlane> {
  return new Promise((resolve) => {
    let tries = 0;
    const check = () => {
      const pp =
        typeof window !== "undefined"
          ? ((window as unknown as Record<string, unknown>)[
              namespace
            ] as PayPalWithFastlane)
          : undefined;
      if (pp?.Fastlane) return resolve(pp);
      if (pp && !pp.Fastlane) return resolve(pp);
      if (++tries >= attempts) return resolve(undefined);
      setTimeout(check, interval);
    };
    check();
  });
}

function FastlaneLoader({
  clientId,
  dataSdkClientToken,
  currency,
}: {
  clientId?: string;
  dataSdkClientToken?: string | null;
  currency?: string;
}) {
  const options: ReactPayPalScriptOptions | null = useMemo(() => {
    if (!clientId || !dataSdkClientToken) return null;
    return {
      clientId,
      currency: currency || "USD",
      intent: "authorize",
      components: "fastlane",
      dataNamespace: FASTLANE_NAMESPACE,
      dataSdkClientToken,
      dataPageType: "checkout",
    };
  }, [clientId, dataSdkClientToken, currency]);

  if (!options) return null;
  return <PayPalScriptProvider options={options} />;
}

// ===================================================================
//  SECTION 2: MAIN REACT COMPONENT
// ===================================================================
export default function ContactInfoForm({
  query,
  control,
  cartToken,
  ref,
  contactErrors,
  setContactErrors,
  fluidShop,
  formEmail,
  collectPhone,
  paymentMethodsIncludes,
  requiredPhone,
  setValue,
  clearErrors,
}: {
  query: QueryObserverResult<CartResponse | null>;
  control: Control<CheckoutFormData>;
  cartToken: string;
  ref: RefObject<HTMLDivElement>;
  contactErrors: ContactErrorsState;
  setContactErrors: React.Dispatch<React.SetStateAction<ContactErrorsState>>;
  fluidShop: string;
  formEmail: string;
  collectPhone: boolean;
  paymentMethodsIncludes: string | null;
  requiredPhone: boolean;
  setValue: UseFormSetValue<CheckoutFormData>;
  clearErrors: UseFormClearErrors<CheckoutFormData>;
}) {
  const { t } = useTranslation();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [emailSuggestion, setEmailSuggestion] = useState<string | null>(null);
  const [showVerificationCode, setShowVerificationCode] = useState(false);
  const [verificationCodeError, setVerificationCodeError] = useState<string>();
  const [paymentFlow, setPaymentFlow] = useState<
    "checking" | "fluid" | "fastlane" | "none"
  >("none");
  const [fastlaneReady, setFastlaneReady] = useState(false);
  const [fastlaneInstance, setFastlaneInstance] =
    useState<FastlaneInstance | null>(null);
  const [eligible, setEligible] = useState<boolean | null>(null);
  const [processing, setProcessing] = useState(false);
  const [ctxId, setCtxId] = useState<string | null>(null);
  const [fastlaneCardComponent, setFastlaneCardComponent] =
    useState<FastlanePaymentComponentInstance | null>(null);
  const [isAwaitingPaymentConfirmation, setIsAwaitingPaymentConfirmation] =
    useState(false);
  const [lastCheckedEmail, setLastCheckedEmail] = useState<string | null>(null);

  const paypalMethod = query.data?.cart?.available_payment_methods?.find(
    (m) => m.type === "paypal",
  );
  const paymentAccountUuid = query.data?.cart?.payment_account;
  const hasPayPal = !!paypalMethod && !!paymentAccountUuid;

  const [sdkToken, setSdkToken] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (!paymentAccountUuid) return;

        const resp = await getPaypalSdkToken({
          paymentAccountId: paymentAccountUuid,
        });
        if (!alive) return;
        const token = resp?.client_sdk?.token || null;

        setSdkToken(token);
      } catch (error) {
        console.error("Error getting paypal sdk token:", error);
        setSdkToken(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, [paymentAccountUuid]);

  useEffect(() => {
    (async () => {
      if (
        !hasPayPal ||
        fastlaneInstance ||
        !sdkToken ||
        !paypalMethod?.client_id
      ) {
        return;
      }

      const pp = (await waitForFastlane(
        FASTLANE_NAMESPACE,
      )) as PayPalWithFastlane;

      if (!pp?.Fastlane) {
        setFastlaneReady(false);
        setEligible(false);
        return;
      }

      try {
        const fl = await pp.Fastlane({
          locale: "en_us",
          metadata: { geoLocOverride: "US" },
          shippingAddressOptions: { allowedLocations: ["US", "CA"] },
        });

        setFastlaneInstance(fl);
        setFastlaneReady(true);
      } catch (error) {
        console.error("Error setting fastlane instance:", error);
        setEligible(false);
        setFastlaneReady(false);
      }
    })();
  }, [hasPayPal, sdkToken, paypalMethod?.client_id, fastlaneInstance]);

  const { updateCartWithPayPalData, updateCartWithShippingOnly } =
    usePayPalCheckout({
      paymentMethodsIncludes,
      fastCheckout: true,
      onSuccess: async () => {},
      onError: (_error) => {
        setProcessing(false);
      },
      onRedirect: (url) => router.push(url),
    });

  const setEmailError = (err?: string) =>
    setContactErrors((prevContactErrors) => ({
      ...prevContactErrors,
      email: err,
    }));
  const setPhoneError = (err?: string) =>
    setContactErrors((prevContactErrors) => ({
      ...prevContactErrors,
      phone: err,
    }));

  const createMagicLinkMutation = useMutation({
    mutationFn: createMagicLink,
    onSuccess: () => {
      setShowVerificationCode(true);
      setPaymentFlow("fluid");
    },
    onError: (_error) => {
      setShowVerificationCode(false);
      setPaymentFlow("none");
    },
  });
  const uuid = createMagicLinkMutation.data?.multi_factor_authentication?.uuid;
  const isCreatingMagicLink = createMagicLinkMutation.isPending;

  const updateContactInfoMutation = useMutation({
    mutationKey: ["update-contact-info", cartToken],
    mutationFn: async ({
      email,
      phone,
    }: {
      email?: string;
      phone?: string;
    }) => {
      const currentEmail = query.data?.cart?.email;
      const cart = query.data?.cart;
      const currentPhone = cart && hasPhone(cart) ? cart.phone : null;
      const payload: Record<string, string> = {};
      if (email !== undefined) payload.email = email;
      else if (currentEmail) payload.email = currentEmail;
      if (phone !== undefined) payload.phone = phone;
      else if (currentPhone) payload.phone = currentPhone;

      return updateCartAttributes({ cartToken, ...payload });
    },
    onError: (error: unknown, variables) => {
      if (isApiError(error)) {
        if (error.data?.errors?.email)
          setEmailError(
            `Email ${
              Array.isArray(error.data.errors.email)
                ? error.data.errors.email[0]
                : error.data.errors.email
            }`,
          );
        if (error.data?.errors?.phone)
          setPhoneError(
            `Phone ${
              Array.isArray(error.data.errors.phone)
                ? error.data.errors.phone[0]
                : error.data.errors.phone
            }`,
          );
      } else {
        if (variables.email !== undefined) setEmailError(t("email_is_invalid"));
        if (variables.phone !== undefined) setPhoneError(t("phone_is_invalid"));
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["cart", cartToken, paymentMethodsIncludes],
      });
      setEmailError(undefined);
      setPhoneError(undefined);
    },
  });

  const {
    mutate: confirmMagicLinkMutation,
    isPending: confirmMagicLinkPending,
  } = useMutation({
    mutationFn: (input: ConfirmMagicLinkInput) =>
      confirmMagicLink(cartToken, input),
    onSuccess: async (data) => {
      if (data.authenticated) {
        setVerificationCodeError(undefined);
        await queryClient.invalidateQueries({
          queryKey: ["cart", cartToken, paymentMethodsIncludes],
        });
        router.push(
          `/checkouts/${cartToken}?jwt=${data.jwt}&fluid_shop=${encodeURIComponent(fluidShop)}`,
        );
      }
    },
    onError: () => {
      setVerificationCodeError(t("invalid_verification_code"));
    },
  });

  const checkPaymentMethodForEmail = async (email: string) => {
    // Prevent duplicate calls if already processing
    if (isCreatingMagicLink) {
      return;
    }

    setPaymentFlow("checking");

    // Normalize country ({ iso: "US" })
    const cartCountry = query.data?.cart?.country?.iso; // "US" | undefined
    const isUS = cartCountry === "US";

    // 1) If no PayPal at all → straight to Fluid
    if (!hasPayPal) {
      try {
        await createMagicLinkMutation.mutateAsync({
          email,
          fluid_shop: fluidShop,
          cart_token: cartToken,
          lookup_only: true,
        });
        setPaymentFlow("fluid");
      } catch {
        setPaymentFlow("none");
      }
      return;
    }

    // 2) If not US → Fluid
    if (!isUS) {
      try {
        await createMagicLinkMutation.mutateAsync({
          email,
          fluid_shop: fluidShop,
          cart_token: cartToken,
          lookup_only: true,
        });
        setPaymentFlow("fluid");
      } catch {
        setPaymentFlow("none");
      }
      return;
    }

    // 3) US + PayPal → try lookup_only first
    try {
      await createMagicLinkMutation.mutateAsync({
        email,
        fluid_shop: fluidShop,
        cart_token: cartToken,
        lookup_only: true,
      });

      setPaymentFlow("fluid");
      setEligible(null);
      setCtxId(null);
      return;
    } catch {
      // 3a) lookup_only failed → try Fastlane identity lookup if SDK is ready
      if (fastlaneReady && fastlaneInstance?.identity) {
        try {
          const { customerContextId } =
            await fastlaneInstance.identity.lookupCustomerByEmail(email);

          if (customerContextId) {
            setPaymentFlow("fastlane");
            setEligible(true);
            setCtxId(customerContextId);
            return;
          }
        } catch {
          // ignore and fall through to Fluid
        }
      }

      // 3b) Final fallback → Fluid magic link
      try {
        await createMagicLinkMutation.mutateAsync({
          email,
          fluid_shop: fluidShop,
          cart_token: cartToken,
          lookup_only: true,
        });
        setPaymentFlow("fluid");
      } catch {
        setPaymentFlow("none");
        setEligible(false);
        setCtxId(null);
      }
    }
  };

  const handleEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const email = e.target.value.trim();
    const cart = query.data?.cart;

    if (email === "" || !/\S+@\S+\.\S+/.test(email)) {
      if (cart?.email !== email) {
        updateContactInfoMutation.mutate({ email: "" });
      }
      setPaymentFlow("none");
      setEmailSuggestion(null);
      setLastCheckedEmail(null);
      return;
    }

    Mailcheck.run({
      email,
      topLevelDomains: [
        ...Mailcheck.defaultTopLevelDomains,
        ...ADDITIONAL_VALID_EMAIL_TLDS,
      ],
      suggested: (suggestion: { full: string }) => {
        setEmailSuggestion(suggestion.full);
      },
      empty: () => {
        setEmailSuggestion(null);
      },
    });

    // Cart update and account lookup are independent — fire-and-forget both.
    // updateContactInfoMutation.onError sets email/phone error state;
    // checkPaymentMethodForEmail internally try/catches every call it makes.
    if (cart?.email !== email) {
      updateContactInfoMutation.mutate({
        email,
        phone: (cart && hasPhone(cart) ? cart.phone : null) || undefined,
      });
    }

    if (lastCheckedEmail !== email) {
      setLastCheckedEmail(email);
      void checkPaymentMethodForEmail(email);
    }
  };

  const handleContinueAsGuest = () => {
    setPaymentFlow("none");
  };

  const handleFastlaneAuthAndRender = async () => {
    if (!fastlaneInstance || !ctxId) {
      return;
    }

    setProcessing(true);

    try {
      const authResponse =
        await fastlaneInstance.identity.triggerAuthenticationFlow(ctxId);

      if (authResponse?.authenticationState !== "succeeded") {
        setProcessing(false);
        return;
      }

      // Store the full profile data
      const profileData = authResponse.profileData;

      // Extract shipping address data
      const shippingAddress = profileData?.shippingAddress;
      const name = shippingAddress?.name || profileData?.name;
      const address = shippingAddress?.address;
      const phoneNumber =
        shippingAddress?.phoneNumber || profileData?.phones?.[0];

      // Extract billing address from card data
      const billingAddress =
        profileData?.card?.paymentSource?.card?.billingAddress;

      if (!name || !address) {
        setProcessing(false);
        return;
      }

      // Build the shipping info object
      const shippingInfo = {
        name: [name.firstName?.trim(), name.lastName?.trim()]
          .filter(Boolean)
          .join(" "),
        email: formEmail,
        address1: address.addressLine1 || "",
        address2: address.addressLine2 || "",
        city: address.adminArea2 || "",
        state: address.adminArea1 || "",
        postal_code: address.postalCode || "",
        country_code: address.countryCode || "US",
      };

      // Build billing info if available
      const billingInfo = billingAddress
        ? {
            name: [name.firstName?.trim(), name.lastName?.trim()]
              .filter(Boolean)
              .join(" "),
            address1: billingAddress.addressLine1 || "",
            address2: billingAddress.addressLine2 || "",
            city: billingAddress.adminArea2 || "",
            state: billingAddress.adminArea1 || "",
            postal_code: billingAddress.postalCode || "",
            country_code: billingAddress.countryCode || "US",
          }
        : undefined;

      // Also update phone if available
      if (phoneNumber?.nationalNumber && collectPhone) {
        const phoneFormatted =
          phoneNumber.countryCode === "1"
            ? phoneNumber.nationalNumber
            : `+${phoneNumber.countryCode}${phoneNumber.nationalNumber}`;

        await updateContactInfoMutation.mutateAsync({
          phone: phoneFormatted,
          email: formEmail,
        });
      }

      // Update cart with both shipping and billing addresses
      if (billingInfo) {
        await updateCartWithPayPalData(
          {
            email: formEmail,
            ship_to: shippingInfo,
            bill_to: billingInfo,
          },
          cartToken,
        );
      } else {
        await updateCartWithShippingOnly(shippingInfo, cartToken);
      }

      const shippingAddressForComponent = {
        firstName: name?.firstName,
        lastName: name?.lastName,
        streetAddress: address?.addressLine1,
        extendedAddress: address?.addressLine2,
        locality: address?.adminArea2,
        region: address?.adminArea1,
        postalCode: address?.postalCode,
        countryCodeAlpha2: address?.countryCode,
      };

      const paymentComponent = await fastlaneInstance.FastlanePaymentComponent({
        shippingAddress: shippingAddressForComponent,
      });

      await paymentComponent.render("#fastlane-card-component-container");
      setFastlaneCardComponent(paymentComponent);
      setIsAwaitingPaymentConfirmation(true);
    } catch (error) {
      console.error("Error fastlane auth and render:", error);
      setProcessing(false);
    } finally {
      setProcessing(false);
    }
  };

  const handleChangeShippingAddress = async () => {
    if (!fastlaneInstance) {
      return;
    }

    try {
      const { selectionChanged, selectedAddress } =
        await fastlaneInstance.profile.showShippingAddressSelector();

      if (
        selectionChanged &&
        selectedAddress &&
        selectedAddress.name &&
        selectedAddress.address
      ) {
        const { name, address } = selectedAddress;

        await updateCartWithPayPalData(
          {
            email: formEmail,
            ship_to: {
              name: [name.firstName?.trim(), name.lastName?.trim()]
                .filter(Boolean)
                .join(" "),
              address1: address.addressLine1 || "",
              address2: address.addressLine2 || "",
              city: address.adminArea2 || "",
              state: address.adminArea1 || "",
              postal_code: address.postalCode || "",
              country_code: address.countryCode || "US",
            },
          },
          cartToken,
        );
      }
    } catch (error) {
      console.error("Error showing shipping address selector:", error);
    }
  };

  const handleFinalizeFastlanePayment = async () => {
    if (!fastlaneCardComponent) {
      console.error("No payment component available for finalization");
      return;
    }

    setProcessing(true);

    try {
      const { id: paymentTokenId } =
        await fastlaneCardComponent.getPaymentToken();
      if (!fastlaneInstance) {
        return;
      }

      if (!paymentTokenId) {
        throw new Error("Failed to get payment token from Fastlane.");
      }

      const fresh = (await queryClient.fetchQuery({
        queryKey: ["cart", cartToken, paymentMethodsIncludes],
      })) as { cart: CartResponse["cart"] };

      const amount = toPaymentAmount(
        fresh.cart.amount_total,
        fresh.cart.currency_decimal_places,
      );
      const shopName = fresh.cart.company?.name || "Order";
      const paymentAccountUuid = fresh.cart.payment_account;

      if (!paymentAccountUuid) {
        throw new Error("PayPal payment account not available");
      }

      const orderRes = await createPaypalOrder({
        cartToken,
        amount,
        description: `${shopName} Order`,
        paymentTokenId,
        fundingSource: "PayPal",
        isPayLater: false,
      });

      if (
        !orderRes?.success ||
        !orderRes?.order_id ||
        !orderRes.authorization_id ||
        !orderRes.payment_uuid
      ) {
        console.error("Failed to create PayPal order:", orderRes);
        throw new Error("Failed to create PayPal order for Fastlane");
      }

      const {
        order_id: orderId,
        authorization_id: authorizationId,
        payment_uuid: paymentUuid,
      } = orderRes;

      await createPaypalPaymentMethod({
        cartToken,
        orderId,
        authorizationId,
        payment_account: paymentAccountUuid,
      });

      const refreshed = (await queryClient.fetchQuery({
        queryKey: ["cart", cartToken, paymentMethodsIncludes],
      })) as { cart: CartResponse["cart"] };

      if (!refreshed.cart.digital_only && !refreshed.cart.shipping_method) {
        const first = refreshed.cart.available_shipping_methods?.[0];
        if (first?.id) {
          await updateShippingMethod({ cartToken, shippingMethodId: first.id });
        }
      }

      const result = await checkoutCart(cartToken, { paymentUuid });

      checkoutRedirect(result, cartToken);
    } catch (error) {
      console.error("Fastlane finalization error:", error);
    } finally {
      setProcessing(false);
    }
  };

  const canShowFastlane = paymentFlow === "fastlane" && fastlaneReady;
  const shippingAddress = query.data?.cart?.ship_to;
  const shippingAddressLocalityLine =
    formatAddressLocalityLine(shippingAddress);

  return (
    <>
      <FastlaneLoader
        clientId={paypalMethod?.client_id}
        dataSdkClientToken={sdkToken}
        currency={query.data?.cart?.currency_code}
      />
      <fieldset className="mt-5">
        <div ref={ref} className="flex flex-row items-center justify-between">
          <legend className="block text-sm/6 font-medium text-gray-900">
            {t("contact")}
          </legend>
          <button
            type="button"
            className="text-sm/6 font-medium text-black underline"
            onClick={() => router.push(`/checkouts/${cartToken}/login`)}
          >
            {t("log_in")}
          </button>
        </div>
        <div className="my-2 space-y-px rounded bg-white shadow-xs">
          <FormTextInput
            control={control}
            name="customer.contact_info.email"
            label={t("email")}
            inputStyles={cn("w-full", collectPhone ? "rounded-t" : "rounded")}
            showErrorMsg={true}
            autoFocus={true}
            onBlur={handleEmailBlur}
            handleInputChange={() => {
              setEmailError(undefined);
              setEmailSuggestion(null);
              clearErrors("customer.contact_info.email");
            }}
          />

          {canShowFastlane && (
            <div className="border-t border-gray-200 bg-gray-50 p-3">
              {eligible === true && ctxId && (
                <>
                  {isAwaitingPaymentConfirmation && shippingAddress && (
                    <div className="mb-4 rounded-md border bg-white p-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-800">
                            {t("ship_to")}
                          </p>
                          <p className="mt-1 text-sm text-gray-600">
                            {shippingAddress.name}
                            <br />
                            {shippingAddress.address1}
                            {shippingAddressLocalityLine && (
                              <>
                                <br />
                                {shippingAddressLocalityLine}
                              </>
                            )}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={handleChangeShippingAddress}
                          className="text-sm font-medium text-blue-600 hover:underline"
                        >
                          {t("change")}
                        </button>
                      </div>
                    </div>
                  )}

                  <div
                    id="fastlane-card-component-container"
                    className="mb-3"
                  />

                  {!isAwaitingPaymentConfirmation ? (
                    <button
                      type="button"
                      onClick={handleFastlaneAuthAndRender}
                      disabled={processing}
                      className="flex w-full items-center justify-center rounded bg-blue-500 px-6 py-3 font-medium whitespace-nowrap text-white transition-colors hover:bg-blue-600 disabled:bg-gray-400"
                    >
                      <span className="mr-2">
                        {processing
                          ? `${t("loading")}...`
                          : `${t("continue_with")}`}
                      </span>
                      <FastlaneWatermark className="h-4" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleFinalizeFastlanePayment}
                      disabled={processing}
                      className="flex w-full items-center justify-center rounded bg-yellow-500 px-6 py-3 font-medium whitespace-nowrap text-black transition-colors hover:bg-yellow-600 disabled:bg-gray-400"
                    >
                      {processing
                        ? `${t("processing")}...`
                        : `${t("pay")} ${
                            query.data?.cart
                              ?.remaining_cart_amount_after_points_in_currency
                              ? query.data?.cart
                                  ?.remaining_cart_amount_after_points_in_currency
                              : query.data?.cart?.amount_total_in_currency
                          }`}
                    </button>
                  )}
                </>
              )}
              {eligible === false && (
                <button
                  type="button"
                  onClick={handleContinueAsGuest}
                  className="w-full rounded bg-blue-500 px-6 py-3 font-medium whitespace-nowrap text-white transition-colors hover:bg-blue-600"
                >
                  {t("continue")}
                </button>
              )}
            </div>
          )}

          {contactErrors.email && (
            <p className="mt-1 px-3 text-sm text-red-500">
              {contactErrors.email}
            </p>
          )}

          {collectPhone && (
            <>
              <FormPhoneInput
                control={control}
                name="customer.contact_info.phone"
                label={t("phone_number")}
                inputStyles="rounded-b-md"
                handleUpdateCartAttributes={(phone) => {
                  const currentEmail = query.data?.cart?.email;
                  updateContactInfoMutation.mutate({
                    phone,
                    email: currentEmail || undefined,
                  });
                }}
                showErrorMsg={true}
                required={requiredPhone}
                showErrorIcon={true}
                country={query.data?.cart?.country?.iso || undefined}
                phoneErrorMessage={contactErrors.phone || undefined}
                handleInputChange={() => {
                  setPhoneError(undefined);
                  clearErrors("customer.contact_info.phone");
                }}
              />
            </>
          )}
        </div>
        {emailSuggestion && (
          <WarningAlert
            className="mt-2"
            message={
              <span className="text-sm">
                {t("did_you_mean", "Did you mean")}{" "}
                <button
                  type="button"
                  className="font-medium underline hover:no-underline"
                  onClick={() => {
                    setValue("customer.contact_info.email", emailSuggestion);
                    const cart = query.data?.cart;
                    const currentPhone =
                      cart && hasPhone(cart) ? cart.phone : null;
                    updateContactInfoMutation.mutate({
                      email: emailSuggestion,
                      phone: currentPhone || undefined,
                    });
                    setEmailSuggestion(null);
                  }}
                >
                  {emailSuggestion}
                </button>
                ?
              </span>
            }
            onDismiss={() => setEmailSuggestion(null)}
          />
        )}
      </fieldset>

      {showVerificationCode && paymentFlow === "fluid" && (
        <VerificationCard
          setOpenDialog={setShowVerificationCode}
          verificationCodeError={verificationCodeError}
          email={formEmail}
          isPending={confirmMagicLinkPending}
          onComplete={(code) => {
            if (uuid) {
              confirmMagicLinkMutation({
                uuid: uuid,
                multi_factor_authentication: { verification_code: code },
              });
            }
          }}
        />
      )}
    </>
  );
}
